import { Suspense, lazy } from 'react'
import { PlainConsole } from './PlainConsole.tsx'
import { AppApiBoundary, configuredBaseUrl } from './api/index.ts'

/**
 * The app is the chrome wrapped around the route table, and the route table comes from the
 * manifest. Nothing here names a screen or a path: if a route is missing from the console,
 * it is missing from `src/nav/manifest.ts`, and that is the only place to look.
 *
 * ---------------------------------------------------------------------------------------------
 * `RealConsole` IS LOADED LAZILY, and measuring why is what makes this comment worth reading
 * rather than skipping. A first version imported it -- and with it `AuthProvider`, `AuthGate`,
 * `useAuth`, everything in `src/auth/**` -- STATICALLY from this file. `App.tsx` is the entry
 * module, so every one of those imports, and `@azure/msal-browser` and `@azure/msal-react`
 * underneath them, ended up in the ENTRY CHUNK regardless of whether `configuredBaseUrl()` was
 * ever going to render that branch. Measured, not assumed: the demo-path entry grew from 864 kB
 * to 1.1 MB, and `PublicClientApplication` was in it in plain text.
 *
 * This is the exact shape of wf-12 E-06, one layer up: a static import defeats a conditional
 * that looks like it should have kept something out of the bundle. The fix is the same fix --
 * `React.lazy` around the branch that needs it, so the mock/demo path (no base URL configured)
 * fetches no MSAL code at all, and only a console pointed at `console-api` ever asks for it.
 * Verified after the fix: 872 kB either way, MSAL in its own 256 kB chunk, zero occurrences of
 * `PublicClientApplication` in the entry under either configuration.
 * ---------------------------------------------------------------------------------------------
 */
const RealConsole = lazy(() => import('./auth/RealConsole.tsx'))

/**
 * While the auth chunk loads. Invisible in practice -- the chunk is same-origin and small -- and
 * present because a blank frame during a real network wait is the exact failure this console
 * keeps writing rules against elsewhere (U-12's "a failure to ask is not an answer of none",
 * one layer up: a failure to have ASKED YET is not a broken screen).
 */
function LoadingAuth() {
  return (
    <div className="page-head" style={{ justifyContent: 'center', minHeight: '70vh', alignItems: 'center' }}>
      <div className="eb">FLEET CONSOLE</div>
    </div>
  )
}

export function App() {
  // The demo tree mounts the seam with NO token source, and that is correct rather than lax:
  // `createFleetApi` returns the mock when no base URL is configured and never asks for a
  // bearer. Passing `resolveToken` here instead would drag `src/auth/**` -- and MSAL behind it
  // -- back into the entry chunk, undoing the split this file exists to keep.
  if (!configuredBaseUrl())
    return (
      <AppApiBoundary>
        <PlainConsole />
      </AppApiBoundary>
    )
  return (
    <Suspense fallback={<LoadingAuth />}>
      <RealConsole />
    </Suspense>
  )
}
